import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
} from '@mui/material';
import {
  Search,
  Cloud,
  Message,
  Description,
  Campaign,
  ArrowForward,
  ExpandMore,
  Facebook,
  Instagram,
  Telegram,
  Language,
  AutoAwesome,
  TableChart,
  ContentCopy,
  CheckCircle,
  WhatsApp,
  Sms,
  Email,
  Public,
  Phone,
  Event,
  SupportAgent,
  Schedule,
  Lock,
} from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const quickStart = [
  { title: 'Create Account', description: 'Sign up at app.cleolead.com and verify your email', tabIndex: 0 },
  { title: 'Connect Platform', description: 'Link Facebook, Instagram, Telegram, WhatsApp, SMS, or Email', tabIndex: 0 },
  { title: 'Configure AI', description: 'Set up prompts, profiles, and import training data', tabIndex: 1 },
  { title: 'Start Chatting', description: 'AI handles customer conversations automatically', tabIndex: 4 },
];

const platformGuides = {
  facebook: {
    title: 'Facebook Messenger',
    icon: <Facebook />,
    color: '#1877F2',
    steps: [
      { label: 'Navigate to Platforms', description: 'Go to Platforms from the sidebar and select the Easy Connect tab.' },
      { label: 'Click Connect', description: 'Find the Facebook Messenger card and click Connect. A Facebook OAuth popup will open.' },
      { label: 'Authorize Your Page', description: 'Log in to Facebook, select the Page you want to connect, and grant all requested message access permissions.' },
      { label: 'Automatic Webhook Setup', description: 'CleoLead automatically configures the webhook for incoming messages. Your page shows a green "Connected" badge.' },
    ],
  },
  instagram: {
    title: 'Instagram Direct',
    icon: <Instagram />,
    color: '#E4405F',
    steps: [
      { label: 'Requirements', description: 'Your Instagram account must be a Professional (Business or Creator) account linked to a Facebook Page.' },
      { label: 'Navigate to Platforms', description: 'Go to Platforms > Easy Connect tab. Find the Instagram Direct card.' },
      { label: 'Authenticate via Facebook', description: 'Click Connect. Instagram uses Meta\'s API, so you\'ll authorize through Facebook OAuth.' },
      { label: 'Select Account', description: 'Choose the linked Instagram Business account and grant DM permissions. Webhook setup is automatic.' },
    ],
  },
  telegram: {
    title: 'Telegram Bot',
    icon: <Telegram />,
    color: '#0088CC',
    steps: [
      { label: 'Create a Bot', description: 'Open Telegram, search for @BotFather, send /newbot, and follow the prompts. Save the API token provided.' },
      { label: 'Navigate to Platforms', description: 'Go to Platforms > Easy Connect tab. Find the Telegram Bot card.' },
      { label: 'Enter Bot Token', description: 'Click Connect and paste your bot token from BotFather. CleoLead registers the webhook with Telegram automatically.' },
      { label: 'Test Your Bot', description: 'Search for your bot on Telegram and send a test message. The AI responds based on your configuration.' },
    ],
  },
  sms: {
    title: 'SMS (Twilio)',
    icon: <Sms />,
    color: '#F22F46',
    steps: [
      { label: 'Get Twilio Credentials', description: 'Sign up at twilio.com and get your Account SID and Auth Token from the Twilio Console.' },
      { label: 'Navigate to Platforms', description: 'Go to Platforms > Easy Connect tab. Find the SMS/Twilio card.' },
      { label: 'Enter Credentials', description: 'Enter your Twilio Account SID, Auth Token, and configure your Twilio phone number for messaging.' },
      { label: 'Test SMS', description: 'Send a test SMS to your configured number. The AI responds to incoming text messages automatically.' },
    ],
  },
  whatsapp: {
    title: 'WhatsApp Business',
    icon: <WhatsApp />,
    color: '#25D366',
    steps: [
      { label: 'Requirements', description: 'You need a WhatsApp Business API account through Meta\'s WhatsApp Business Platform.' },
      { label: 'Navigate to Platforms', description: 'Go to Platforms > Easy Connect tab. Find the WhatsApp Business card.' },
      { label: 'Enter API Credentials', description: 'Provide your WhatsApp Business API credentials including your phone number ID and access token.' },
      { label: 'Verify Connection', description: 'Your WhatsApp Business number shows as active. Test by sending a message to your business number.' },
    ],
  },
  email: {
    title: 'Email (SendGrid)',
    icon: <Email />,
    color: '#1A82E2',
    steps: [
      { label: 'Get SendGrid API Key', description: 'Sign up at sendgrid.com and create an API key with Mail Send permissions.' },
      { label: 'Navigate to Platforms', description: 'Go to Platforms > Easy Connect tab. Find the Email/SendGrid card.' },
      { label: 'Configure Email', description: 'Enter your SendGrid API key, configure your sender email address, and verify your sending domain.' },
      { label: 'Test', description: 'Send a test email to verify the connection. Incoming emails to your configured address are handled by the AI.' },
    ],
  },
  websiteWidget: {
    title: 'Website Widget',
    icon: <Language />,
    color: '#10B981',
    steps: [
      { label: 'Open Widget Tab', description: 'Go to Platforms > Website Widget tab.' },
      { label: 'Choose a Theme', description: 'Select from 7 themes: Light, Dark Mode, Ocean Blue, Warm Sunset, Nature Green, Royal Purple, or Custom. The live preview updates instantly.' },
      { label: 'Configure Settings', description: 'Set the Widget Title, Greeting Message, and Input Placeholder text.' },
      { label: 'Copy & Embed', description: 'Click Copy Code and paste the script before the closing </body> tag on your website.' },
    ],
  },
  globeWidget: {
    title: 'Globe Widget',
    icon: <Public />,
    color: '#8B5CF6',
    steps: [
      { label: 'Open Globe Widget Tab', description: 'Go to Platforms > Globe Widget tab for the interactive 3D globe chat interface.' },
      { label: 'Customize Appearance', description: 'Configure the globe widget colors, behavior, and style to match your website.' },
      { label: 'Copy Embed Code', description: 'Copy the generated embed code for your globe widget configuration.' },
      { label: 'Install on Your Site', description: 'Paste the script into your website HTML before the closing </body> tag.' },
    ],
  },
};

const aiSettingsGuides = [
  {
    id: 'prompts',
    icon: <AutoAwesome />,
    title: 'System Prompts',
    description: 'Create and manage prompts that define your AI\'s personality and behavior',
    content: [
      'Navigate to **AI Settings** from the sidebar — the **Prompts** tab opens by default.',
      'View your prompt inventory with stats: Total Prompts, Active Prompts, and Total Usage.',
      'Each prompt shows Name, Category, Temperature, Max Tokens, Usage count, and Status.',
      'Click **+ New Prompt** to create manually, or **Generate with AI** to auto-generate from your business description.',
      'For AI generation: enter company name, products/services, target audience, and desired tone.',
      'Configure: Category (General/Human/Formal/Casual), Temperature (0–1.0), Max Tokens (50–500).',
      'Adjust personality sliders: Text Length, Humor, Confidence, Friendliness (each 1–10).',
      'Click **Load from Preset** to start with a pre-built industry template.',
      'Activate a prompt with the checkmark icon — only one prompt can be active at a time.',
    ],
  },
  {
    id: 'profiles',
    icon: <Description />,
    title: 'AI Profiles',
    description: 'Configure expert personas with name, role, specializations, and personality traits',
    content: [
      'Click the **Profiles** tab in AI Settings.',
      'View stats: Total Profiles, Active Profiles, Training Dialogs, Total Usage.',
      'Click **+ New Profile** or **Generate with AI** to create a new profile.',
      'Choose from 8 templates: Sales Closer, Support Specialist, Business Consultant, and more.',
      'Configure: name, description, business category, expertise level.',
      'Set the AI persona with expert name, role, and specializations.',
      'Adjust personality traits and tone settings to match your brand voice.',
    ],
  },
  {
    id: 'training',
    icon: <Message />,
    title: 'Training with Conversations',
    description: 'Import real conversations to teach the AI your communication style',
    content: [
      'Click the **Training** tab in AI Settings.',
      'View training stats: Training Dialogs, Examples (Few-shot), Total Messages.',
      'Click **Import Conversations** to upload real conversation data.',
      'The AI automatically scores each conversation for quality (1–10).',
      'Star your best 2–3 conversations to use as **few-shot examples**.',
      'Starred examples are injected directly into AI prompts for style matching.',
      'Export training data as JSONL for use with external fine-tuning platforms.',
    ],
  },
  {
    id: 'avatar',
    icon: <Description />,
    title: 'AI Avatar',
    description: 'Set the AI\'s profile picture and persona name for conversations',
    content: [
      'Click the **Avatar** tab in AI Settings.',
      'Upload a custom profile picture for your AI assistant.',
      'Set the persona name displayed in the chat widget and conversations.',
      'The avatar and name from your active prompt appear in the widget.',
    ],
  },
];

const chatHistoryGuide = {
  sections: [
    {
      title: 'Viewing Conversations',
      content: 'Navigate to Chat History from the sidebar. The table displays all conversations with User, Platform, Assigned To, Profile, Messages count, Status, Created, and Last Updated.',
    },
    {
      title: 'Status Filters',
      content: 'Filter by status: All, Active, Pending, Closed, Sales Pending, Error, or Data Collected. "Data Collected" means the AI gathered all required form data. "Sales Pending" flags conversations needing human follow-up.',
    },
    {
      title: 'Platform & Assignment Filters',
      content: 'Use Platform Tabs to filter by All Platforms or a specific connected page. Assignment Filters (All, Mine, Unassigned) help manage team workload.',
    },
    {
      title: 'Conversation Details',
      content: 'Click Open on any conversation to view the full message history with timestamps and sources. You can see all messages exchanged between the customer and AI.',
    },
    {
      title: 'Manual Replies',
      content: 'Open a conversation and type your reply in the message input field at the bottom. Messages are sent to the customer on their original platform.',
    },
    {
      title: 'Sync & Search',
      content: 'Click Sync Conversations to pull the latest messages from all platforms. Use the search bar to find conversations by username, ID, or platform.',
    },
  ],
};

const broadcastGuide = {
  sections: [
    {
      title: 'Compose Your Message',
      content: 'Navigate to Broadcast from the sidebar. Type your message in the composer. Click "Enhance with AI" to optimize it for engagement and clarity.',
    },
    {
      title: 'Select Recipients',
      content: 'Use Platform Filter tabs (All, Instagram, Facebook) to narrow the client list. Select recipients with checkboxes. The "Send to N Clients" button shows your count.',
    },
    {
      title: '24-Hour Messaging Window',
      content: 'Meta platforms (Facebook, Instagram) restrict messaging to clients who interacted in the last 24 hours. Clients outside this window show a warning icon. Telegram has no restrictions.',
    },
    {
      title: 'Send & Track',
      content: 'Review your message and recipients, then click Send. CleoLead spaces out messages to respect platform rate limits. Broadcast history tracks success/failure per recipient.',
    },
  ],
};

const dataImportGuide = {
  tabs: [
    { name: 'Documents', content: 'Upload PDF, DOC, DOCX, TXT, MD, CSV, or JSON files (max 500MB each). Drag and drop or browse. The AI automatically processes and indexes content for use in conversations.' },
    { name: 'Web URLs', content: 'Add website URLs with multi-page crawling (2–50 pages). Supports automatic sitemap detection for comprehensive content indexing.' },
    { name: 'Instructions', content: 'Write custom business rules, guidelines, and response policies. Instructions take priority over document content when the AI generates responses.' },
    { name: 'Products & Services', content: 'Add your product catalog with names, descriptions, and pricing. Enables the Automatic Sales feature on the Dashboard.' },
    { name: 'Product Feeds', content: 'Bulk-import products from Shopify CSV, WooCommerce XML, or Google Merchant XML feed formats.' },
  ],
};

const dataExportGuide = {
  tabs: [
    { name: 'Completed Forms', content: 'View and export form submissions from chatbot conversations. Filter by Completed, Incomplete, or All status. Export to CSV or JSON.' },
    { name: 'Google Sheets', content: 'Connect your Google account via OAuth. Select or create a target spreadsheet. Configure scheduled sync for automatic updates.' },
    { name: 'CMS Export', content: 'Integrate with CRM systems like HubSpot. Configure field mapping and auto-sync triggers with custom webhooks.' },
  ],
};

// AI Voice — inbound + outbound calling
const voiceGuide = {
  intro:
    'CleoLead answers and places real phone calls with a natural AI voice. It uses your business knowledge base, books appointments, captures details, and hands off to a human when needed.',
  sections: [
    {
      title: 'Get a Phone Number',
      content:
        'Go to Voice > Phone Numbers, choose a country and area code, and provision a number (powered by Twilio). Each paid plan includes at least one number; additional numbers are metered.',
    },
    {
      title: 'Inbound Receptionist',
      content:
        'Incoming calls are answered by the AI receptionist. It greets callers in your brand voice, answers questions from your knowledge base, and can book reservations or take messages. Configure the greeting and behavior in Voice > Settings.',
    },
    {
      title: 'Outbound Calls',
      content:
        'The AI can place outbound calls — follow-ups, confirmations, and reminders. Outbound campaigns use a propose-then-approve flow: you review the call list and script before anything dials. Per-call instructions never overwrite your standing voice prompt.',
    },
    {
      title: 'Call Transfer & Handoff',
      content:
        'Set transfer rules in Voice > Routing — route by intent (urgent / billing / sales), time of day, or as a fallback when the AI is uncertain. Calls can warm-transfer to a human line.',
    },
    {
      title: 'Voicemail & Transcripts',
      content:
        'Missed calls capture voicemail. Every call is transcribed and summarized; captured details flow into the conversation record and forms, just like chat.',
    },
  ],
};

// Reservations + Calendar
const reservationsGuide = {
  intro:
    'CleoLead turns conversations and calls into real bookings — synced two ways with your calendar and reservation system, so the AI always sees true availability and your tools always reflect AI-made bookings.',
  sections: [
    {
      title: 'Google Calendar (2-way sync)',
      content:
        'Go to Reservations > Calendar and connect Google Calendar via OAuth. The AI proposes only genuinely free slots and writes confirmed bookings back to your calendar. Changes on either side stay in sync.',
    },
    {
      title: 'Wix Table Reservations',
      content:
        'Restaurants using Wix can sync table reservations. Connect the Wix app once — diners booking on your Wix site appear in CleoLead, and AI-made table bookings push back to Wix. Note: Wix has two reservation products; CleoLead syncs Wix Table Reservations.',
    },
    {
      title: 'How Bookings Are Routed',
      content:
        'Table bookings go to Wix; general appointments go to Google Calendar; voicemail-only calls create no booking. The AI picks the right destination automatically based on intent — no double-booking.',
    },
  ],
};

// Owner assistant, flow graph, profiles/Trends, AI Analyst, lead gen, products, team
const platformFeatureGuides = [
  {
    id: 'cleo-assistant',
    icon: <AutoAwesome />,
    title: 'CleoLead AI — your owner assistant',
    description: 'A built-in assistant that knows your whole business and helps you run it by chat.',
    content: [
      'Open **CleoLead AI** from the sidebar. It has read access to your conversations, clients, bookings, and settings.',
      'Ask it questions in plain language — "How did sales go this week?", "Which leads are hot?", "Summarize today\'s calls."',
      'Upload files (PDFs, spreadsheets, docs) directly in the chat for it to read and reason over.',
      'Get daily briefings: a short morning summary of what happened and what needs your attention.',
      'It is read-and-advise focused — it surfaces insights and drafts, you stay in control of changes.',
    ],
  },
  {
    id: 'flow-graph',
    icon: <AutoAwesome />,
    title: 'Conversation Flow & Data Collection',
    description: 'Design how the AI guides conversations: intents, question groups, and forms.',
    content: [
      'Open the **Conversation Flow** view to see how chats branch by intent (e.g. booking, support, pricing).',
      'Each intent can have **Question Groups** — the specific things the AI should find out (name, party size, date, budget).',
      'Answers are captured into **Forms**. When all required fields are gathered, the conversation is marked **Data Collected**.',
      'Collected form data exports to Google Sheets or your CRM automatically (see Data Management).',
      'The flow graph is a live, deterministic view — it shows the real branching without changing anything until you edit it.',
    ],
  },
  {
    id: 'profiles-trends',
    icon: <Description />,
    title: 'Client Profiles & Trends',
    description: 'A living profile for every contact, enriched from every conversation.',
    content: [
      'Open **Clients** to see a profile per contact: name, channel, contact details, interests, key facts, and status (lead → prospect → active → converted).',
      'Profiles are enriched automatically from conversations — the AI extracts key facts, sentiment, and purchase intent.',
      'Open the **Trends** tab on a profile to see engagement, sentiment, and message activity over time.',
      'The **AI Analyst** tab gives a plain-language read on the relationship and suggested next steps.',
      'Filter clients by status, channel, or project to focus your follow-up.',
    ],
  },
  {
    id: 'lead-gen',
    icon: <Campaign />,
    title: 'Lead Generation',
    description: 'Find and import prospects, then work them across every channel.',
    content: [
      'Open **Lead Generator** to build prospect lists for your ideal customer profile.',
      'Imported leads become Client Profiles with status **lead** and any contact info attached.',
      'Reach out by the channels you have connected — email (SMTP), or social DMs where available.',
      'Track which contact methods are available under Clients — the app tells you what setup each channel needs.',
    ],
  },
  {
    id: 'products-payments',
    icon: <TableChart />,
    title: 'Products & Payment Links',
    description: 'Give the AI a catalog so it can recommend and sell in chat.',
    content: [
      'Add products under **Data Import > Products & Services** (name, description, price), or bulk-import a feed (Shopify CSV, WooCommerce/Google Merchant XML).',
      'With a catalog in place, the AI can recommend the right product mid-conversation.',
      'The AI can share **payment links** in chat to close the sale without leaving the thread.',
      'Use **Review-before-send** mode if you want to approve sales messages before they go out.',
    ],
  },
  {
    id: 'team-roles',
    icon: <CheckCircle />,
    title: 'Team, Roles & Demo Pages',
    description: 'Invite teammates, assign conversations, and share demos.',
    content: [
      'Invite teammates and assign roles so the right people can manage conversations and settings.',
      'Assign conversations to specific team members; filter Chat History by Mine / Unassigned to manage workload.',
      'Spin up **Demo Pages** to show a live AI chat experience to a prospect or stakeholder.',
      'Account owners manage billing and subscription; members focus on day-to-day conversations.',
    ],
  },
];

// FAQ — absorbs the former Help Center (account/billing, data/GDPR,
// channel connection, troubleshooting) into the merged docs destination.
const faqCategories: { title: string; icon: React.ReactNode; items: { q: string; a: string }[] }[] = [
  {
    title: 'Getting Started',
    icon: <AutoAwesome />,
    items: [
      {
        q: 'How do I create my first AI assistant?',
        a: 'Sign up at app.cleolead.com, verify your email, then follow the setup: connect a channel, add some business knowledge (documents, website, or instructions), and configure a prompt. The AI starts handling conversations right away.',
      },
      {
        q: 'How do I test responses before going live?',
        a: 'Use the AI Test Chat in your dashboard to send sample messages and see how the AI replies based on your current prompt and knowledge base. Adjust prompts and training, then connect a live channel when you are happy.',
      },
      {
        q: 'Does CleoLead speak other languages?',
        a: 'Yes. The AI detects and replies in the customer\'s language automatically across chat and voice — no separate setup per language.',
      },
    ],
  },
  {
    title: 'Account & Billing',
    icon: <CheckCircle />,
    items: [
      {
        q: 'How do I upgrade or change my plan?',
        a: 'Go to Settings > Subscription > Change Plan, pick your plan, and confirm. Upgrades take effect immediately and are prorated for the current billing period. Billing is handled securely through Stripe.',
      },
      {
        q: 'How do I update my payment details?',
        a: 'Go to Settings > Billing > Payment Methods to add or update your card. We never see or store full card numbers — payments are processed by Stripe.',
      },
      {
        q: 'What happens when I hit my message limit?',
        a: 'You get email notifications at 80% and 100% of your plan limit. At 100%, AI responses pause until your next billing cycle or until you upgrade. Voice and SMS bundles are metered separately with predictable overage pricing.',
      },
      {
        q: 'Do you have a plan smaller than Pro?',
        a: 'No. CleoLead offers three premium tiers built for businesses that want a full AI business manager — there is no entry-level/starter tier.',
      },
    ],
  },
  {
    title: 'Connecting Channels',
    icon: <Cloud />,
    items: [
      {
        q: 'Why don\'t my Facebook / Instagram Pages show up when I connect?',
        a: 'During our Meta review period, Facebook and Instagram permissions are granted only to people with a role on our Meta app. Until app review completes, you must be added as a Tester on the CleoLead Meta app (and accept the invite) before connecting — then your Pages appear and messaging works. Contact support and we\'ll add you.',
      },
      {
        q: 'What do I need to connect Telegram?',
        a: 'Just a bot token. Open Telegram, message @BotFather, send /newbot, copy the token, and paste it into Platforms > Easy Connect > Telegram. CleoLead registers the webhook automatically.',
      },
      {
        q: 'How do SMS and voice numbers work?',
        a: 'Phone numbers are provisioned through Twilio inside the app (Voice > Phone Numbers). Pick a country/area code and provision — no separate Twilio setup needed for standard numbers.',
      },
      {
        q: 'What\'s required to sync Wix reservations?',
        a: 'Connect the CleoLead Wix app and grant the "Manage Reservations" scope. Once granted, Wix Table Reservations sync two ways with CleoLead.',
      },
    ],
  },
  {
    title: 'Data, Privacy & GDPR',
    icon: <Description />,
    items: [
      {
        q: 'Where does my data go, and who processes it?',
        a: 'Your data stays in your account and is used only to run your AI. We use a short list of sub-processors (e.g. our AI provider, email/SMS/voice, payments, hosting) — see our Privacy Policy for the current list. We do not sell your data.',
      },
      {
        q: 'Can I export my data?',
        a: 'Yes. Conversations, form submissions, and client data export to CSV/JSON, Google Sheets, or your CRM. Available export formats scale with your plan.',
      },
      {
        q: 'How do I make a GDPR / data request?',
        a: 'Use the support form below (or email support@cleolead.com) with the details of your request. See our GDPR and Privacy pages for how we handle personal data and your rights.',
      },
    ],
  },
  {
    title: 'Troubleshooting',
    icon: <CheckCircle />,
    items: [
      {
        q: 'The AI isn\'t responding to messages — what should I check?',
        a: '1) The channel is connected and shows as active. 2) You have messages remaining on your plan. 3) The AI is enabled for that project. 4) Check the Status page for any incidents. If it persists, contact support with the channel and a sample message.',
      },
      {
        q: 'A connected page suddenly stopped working.',
        a: 'Channel tokens (especially Meta) can expire or be revoked. Reconnect the channel from Platforms > Easy Connect. For Facebook/Instagram, re-accept the full consent dialog including the per-Page opt-in step.',
      },
      {
        q: 'Bookings aren\'t showing in my calendar.',
        a: 'Confirm the calendar/reservation integration is connected and authorized. Google Calendar and Wix both use OAuth — if access was revoked, reconnect. Table bookings route to Wix; general appointments route to Google Calendar.',
      },
    ],
  },
];

interface PopularArticle {
  title: string;
  category: string;
  tabIndex: number;
  platform?: string;
  guideId?: string;
}

const popularArticles: PopularArticle[] = [
  { title: 'Connecting Facebook Messenger', category: 'Platforms', tabIndex: 0, platform: 'facebook' },
  { title: 'Setting up AI prompts for your business', category: 'AI Settings', tabIndex: 1, guideId: 'prompts' },
  { title: 'Importing conversations for AI training', category: 'AI Settings', tabIndex: 1, guideId: 'training' },
  { title: 'Managing chat history and status filters', category: 'Chat History', tabIndex: 4 },
  { title: 'Exporting data to Google Sheets', category: 'Data', tabIndex: 6 },
  { title: 'AI voice: inbound & outbound calls', category: 'AI Voice', tabIndex: 2 },
  { title: 'Syncing reservations with Wix & Google Calendar', category: 'Reservations', tabIndex: 3 },
];

const renderTextWithBold = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};


const DocumentationPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState<keyof typeof platformGuides>('facebook');
  const [expandedGuide, setExpandedGuide] = useState<string | false>(false);
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  // Inline support form — posts to the same public endpoint the pricing
  // Contact-Sales form uses, with type="support".
  const [support, setSupport] = useState({ name: '', email: '', message: '' });
  const [supportState, setSupportState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [supportError, setSupportError] = useState('');

  const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!support.name.trim() || !isEmail(support.email) || !support.message.trim()) {
      setSupportError('Please add your name, a valid email, and a message.');
      setSupportState('error');
      return;
    }
    setSupportState('sending');
    setSupportError('');
    try {
      const res = await fetch(
        'https://cleolead-api-743703226990.us-central1.run.app/api/v1/public/contact-sales',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: support.name.trim(),
            email: support.email.trim(),
            message: support.message.trim(),
            type: 'support',
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to send your message.');
      }
      setSupportState('sent');
      setSupport({ name: '', email: '', message: '' });
    } catch (err) {
      setSupportError(err instanceof Error ? err.message : 'Failed to send your message. Please try again.');
      setSupportState('error');
    }
  };

  const handleArticleClick = (article: PopularArticle) => {
    setActiveTab(article.tabIndex);
    if (article.platform) {
      setSelectedPlatform(article.platform as keyof typeof platformGuides);
    }
    if (article.guideId) {
      setExpandedGuide(article.guideId);
    }
    setTimeout(() => {
      document.getElementById('docs-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const filteredArticles = searchQuery
    ? popularArticles.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : popularArticles;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Documentation" color="primary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              How can we help you?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Step-by-step guides to help you get the most out of CleoLead.
            </Typography>
            <TextField
              placeholder="Search documentation..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: '100%', maxWidth: 600 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Quick Start */}
      <Box sx={{ pb: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Quick Start Guide
          </Typography>
          <Grid container spacing={3}>
            {quickStart.map((step, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' },
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => {
                    setActiveTab(step.tabIndex);
                    document.getElementById('docs-tabs')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Main Documentation Tabs */}
      <Box id="docs-tabs" sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Platforms" icon={<Cloud />} iconPosition="start" />
            <Tab label="AI Settings" icon={<AutoAwesome />} iconPosition="start" />
            <Tab label="AI Voice" icon={<Phone />} iconPosition="start" />
            <Tab label="Reservations" icon={<Event />} iconPosition="start" />
            <Tab label="Chat History" icon={<Message />} iconPosition="start" />
            <Tab label="Broadcasts" icon={<Campaign />} iconPosition="start" />
            <Tab label="Data Management" icon={<TableChart />} iconPosition="start" />
            <Tab label="Sales & Growth" icon={<AutoAwesome />} iconPosition="start" />
          </Tabs>

          {/* Tab 0: Platform Connections */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Connect Your Messaging Platforms
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              CleoLead connects to 6 messaging channels via Easy Connect, plus two website widget options. Select a platform below for setup instructions.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Easy Connect Platforms
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {Object.entries(platformGuides).slice(0, 6).map(([key, platform]) => (
                <Grid size={{ xs: 6, sm: 4, md: 2 }} key={key}>
                  <Card
                    onClick={() => setSelectedPlatform(key as keyof typeof platformGuides)}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selectedPlatform === key ? platform.color : 'divider',
                      bgcolor: selectedPlatform === key ? `${platform.color}15` : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: platform.color },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2, px: 1 }}>
                      <Box sx={{ color: platform.color, mb: 0.5 }}>{platform.icon}</Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {platform.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Website Widgets
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {Object.entries(platformGuides).slice(6).map(([key, platform]) => (
                <Grid size={{ xs: 6, sm: 4, md: 2 }} key={key}>
                  <Card
                    onClick={() => setSelectedPlatform(key as keyof typeof platformGuides)}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selectedPlatform === key ? platform.color : 'divider',
                      bgcolor: selectedPlatform === key ? `${platform.color}15` : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: platform.color },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2, px: 1 }}>
                      <Box sx={{ color: platform.color, mb: 0.5 }}>{platform.icon}</Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {platform.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Step-by-step guide for selected platform */}
            <Card sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ color: platformGuides[selectedPlatform].color }}>
                  {platformGuides[selectedPlatform].icon}
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {platformGuides[selectedPlatform].title} Setup
                </Typography>
              </Box>

              <Stepper orientation="vertical">
                {platformGuides[selectedPlatform].steps.map((step, index) => (
                  <Step key={index} active>
                    <StepLabel>
                      <Typography fontWeight="bold">{step.label}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography color="text.secondary">{step.description}</Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {selectedPlatform === 'websiteWidget' && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Embed Code Example:
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#1a1a2e',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      overflow: 'auto',
                    }}
                  >
                    <code style={{ color: '#a5d6ff' }}>
                      {`<script src="https://widget.cleolead.com/chat.js"\n  data-project-id="YOUR_PROJECT_ID"\n  data-api-key="YOUR_API_KEY"\n  data-position="bottom-right">\n</script>`}
                    </code>
                  </Box>
                  <Button startIcon={<ContentCopy />} variant="outlined" size="small" sx={{ mt: 2 }}>
                    Copy Code
                  </Button>
                </Box>
              )}
            </Card>

            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Tip:</strong> Facebook and Instagram require a Meta Developer App. Telegram only needs a bot token from @BotFather. The system handles webhook configuration automatically for all Easy Connect platforms.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 1: AI Settings */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              AI Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Configure your AI assistant's personality, knowledge, and behavior. AI Settings has four tabs: Prompts, Profiles, Training, and Avatar.
            </Typography>

            <Alert severity="info" sx={{ mb: 4 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Start by generating a prompt with AI — describe your business and it creates a customized system prompt. Then import your best conversations for training.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {aiSettingsGuides.map((guide) => (
                <Grid size={{ xs: 12 }} key={guide.id}>
                  <Accordion
                    expanded={expandedGuide === guide.id}
                    onChange={(_, isExpanded) => setExpandedGuide(isExpanded ? guide.id : false)}
                    sx={{ bgcolor: 'background.default', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40, height: 40, borderRadius: 1, bgcolor: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                          }}
                        >
                          {guide.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">{guide.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{guide.description}</Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ pl: 7 }}>
                        <List dense>
                          {guide.content.map((step, i) => (
                            <ListItem key={i} sx={{ px: 0, alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontSize: '0.7rem', fontWeight: 'bold',
                                  }}
                                >
                                  {i + 1}
                                </Box>
                              </ListItemIcon>
                              <ListItemText primary={renderTextWithBold(step)} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>Temperature Guide:</strong> Lower values (0.2) give consistent, predictable responses. Higher values (0.8) add creativity. Start at 0.5 and adjust based on your needs.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 2: AI Voice */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              AI Voice — Inbound &amp; Outbound Calls
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {voiceGuide.intro}
            </Typography>

            <Grid container spacing={3}>
              {voiceGuide.sections.map((section, index) => (
                <Grid size={{ xs: 12, md: 6 }} key={index}>
                  <Card sx={{ height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Phone color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">{section.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{section.content}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Numbers:</strong> Phone numbers are provisioned through Twilio inside the app. Each paid plan includes at least one number; additional numbers and call/SMS minutes are metered with predictable pricing.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 3: Reservations & Calendar */}
          <TabPanel value={activeTab} index={3}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Reservations &amp; Calendar
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {reservationsGuide.intro}
            </Typography>

            <Grid container spacing={3}>
              {reservationsGuide.sections.map((section, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Card sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Event color="primary" fontSize="small" sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{section.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{section.content}</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Wix setup:</strong> Connect the CleoLead Wix app and grant the <strong>Manage Reservations</strong> scope to enable two-way Wix Table Reservations sync.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 4: Chat History */}
          <TabPanel value={activeTab} index={4}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Chat History
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              View and manage all customer conversations across connected platforms from a single dashboard.
            </Typography>

            <Grid container spacing={3}>
              {chatHistoryGuide.sections.map((section, index) => (
                <Grid size={{ xs: 12, md: 6 }} key={index}>
                  <Card sx={{ height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CheckCircle color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">{section.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{section.content}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Note:</strong> Manual replies are sent through the same platform the customer originally used. The table sorts by Last Updated, so the most recent activity appears first.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 5: Broadcasts */}
          <TabPanel value={activeTab} index={5}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Broadcast Messages
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Send messages to multiple clients at once across connected platforms. Available on Professional plan and above.
            </Typography>

            <Grid container spacing={3}>
              {broadcastGuide.sections.map((section, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Card sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', flexShrink: 0,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">{index + 1}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{section.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{section.content}</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Platform Limits:</strong> Meta platforms (Facebook, Instagram) restrict messaging to clients who interacted within 24 hours. Telegram has no messaging time restrictions.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 6: Data Management */}
          <TabPanel value={activeTab} index={6}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Data Management
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Import knowledge to train your AI and export collected data to your tools.
            </Typography>

            {/* Data Import Section */}
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Data Import
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload documents, website URLs, custom instructions, and products to build your AI's knowledge base via RAG (Retrieval-Augmented Generation).
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {dataImportGuide.tabs.map((tab, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Card sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{tab.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{tab.content}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Data Export Section */}
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
              Data Export
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Export customer data, form submissions, and conversation data in multiple formats.
            </Typography>

            <Grid container spacing={2}>
              {dataExportGuide.tabs.map((tab, index) => (
                <Grid size={{ xs: 12, sm: 4 }} key={index}>
                  <Card sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{tab.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{tab.content}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Plan Limits:</strong> Professional includes CSV, JSON, and API exports. Business adds SQL. Enterprise unlocks all formats plus custom CRM integrations. Document and URL import limits also scale with your plan.
                </Typography>
              </Alert>
            </Box>
          </TabPanel>

          {/* Tab 7: Sales & Growth (owner assistant, flow graph, profiles, lead gen, products, team) */}
          <TabPanel value={activeTab} index={7}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Sales &amp; Growth
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Beyond answering messages, CleoLead helps you run and grow the business — an owner assistant, conversation design, client intelligence, lead generation, and selling in chat.
            </Typography>

            <Grid container spacing={3}>
              {platformFeatureGuides.map((guide) => (
                <Grid size={{ xs: 12 }} key={guide.id}>
                  <Accordion
                    expanded={expandedGuide === guide.id}
                    onChange={(_, isExpanded) => setExpandedGuide(isExpanded ? guide.id : false)}
                    sx={{ bgcolor: 'background.default', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40, height: 40, borderRadius: 1, bgcolor: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                          }}
                        >
                          {guide.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">{guide.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{guide.description}</Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ pl: 7 }}>
                        <List dense>
                          {guide.content.map((step, i) => (
                            <ListItem key={i} sx={{ px: 0, alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontSize: '0.7rem', fontWeight: 'bold',
                                  }}
                                >
                                  {i + 1}
                                </Box>
                              </ListItemIcon>
                              <ListItemText primary={renderTextWithBold(step)} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        </Container>
      </Box>

      {/* FAQ — absorbed from the former Help Center */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
              Help Center
            </Typography>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
              Frequently asked questions
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 680 }}>
              Account, billing, channel setup, data &amp; privacy, and troubleshooting — the
              answers most people are looking for.
            </Typography>
          </Reveal>

          <Grid container spacing={4}>
            {faqCategories.map((cat, ci) => (
              <Grid size={{ xs: 12, md: 6 }} key={cat.title}>
                <Reveal delay={ci * 70}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: `${tokens.radiusControl}px`,
                        bgcolor: tokens.copperTint, color: tokens.copper,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {cat.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold">{cat.title}</Typography>
                  </Box>
                  {cat.items.map((item, ii) => (
                    <Accordion
                      key={ii}
                      expanded={expandedFaq === `${cat.title}-${ii}`}
                      onChange={(_, isExpanded) =>
                        setExpandedFaq(isExpanded ? `${cat.title}-${ii}` : false)
                      }
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>{item.q}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">{item.a}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Support — posts to the same endpoint as the pricing Contact-Sales form (type=support) */}
      <Box id="contact-support" sx={{ py: 8, bgcolor: 'background.paper', borderTop: `1px solid ${tokens.border}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 5 }}>
              <Reveal>
                <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
                  Still stuck?
                </Typography>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Contact support
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Can&apos;t find your answer above? Send us a message and a human will get
                  back to you — typically within 24 hours.
                </Typography>
                <List dense sx={{ mb: 1 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 34 }}><Schedule color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Response within 24 hours"
                      secondary="Business & Enterprise plans get priority support."
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 34 }}><Email color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Prefer email?"
                      secondary="Write to support@cleolead.com directly."
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 34 }}><Lock color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Billing, account & data requests"
                      secondary="Include order/account details so we can help faster."
                    />
                  </ListItem>
                </List>
              </Reveal>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Reveal delay={70}>
                <Card sx={{ p: { xs: 3, md: 4 } }}>
                  {supportState === 'sent' ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <Box sx={{ color: 'success.main', mb: 1.5, display: 'inline-flex' }}>
                        <CheckCircle sx={{ fontSize: 56 }} />
                      </Box>
                      <Typography variant="h6" gutterBottom>Message sent</Typography>
                      <Typography color="text.secondary">
                        Thanks for reaching out — we&apos;ll get back to you within 24 hours.
                      </Typography>
                    </Box>
                  ) : (
                    <Box component="form" onSubmit={handleSupportSubmit}>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Send us a message
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Tell us what you need help with and we&apos;ll take it from there.
                      </Typography>

                      {supportState === 'error' && (
                        <Alert severity="error" sx={{ mb: 2 }}>{supportError}</Alert>
                      )}

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Your name"
                            fullWidth
                            required
                            value={support.name}
                            onChange={(e) => setSupport({ ...support, name: e.target.value })}
                            disabled={supportState === 'sending'}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Email address"
                            type="email"
                            fullWidth
                            required
                            value={support.email}
                            onChange={(e) => setSupport({ ...support, email: e.target.value })}
                            disabled={supportState === 'sending'}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="How can we help?"
                            fullWidth
                            required
                            multiline
                            rows={4}
                            value={support.message}
                            onChange={(e) => setSupport({ ...support, message: e.target.value })}
                            disabled={supportState === 'sending'}
                            placeholder="Describe your issue, question, or request…"
                          />
                        </Grid>
                      </Grid>

                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={<SupportAgent />}
                        disabled={supportState === 'sending'}
                        sx={{ mt: 3 }}
                      >
                        {supportState === 'sending' ? 'Sending…' : 'Send message'}
                      </Button>
                    </Box>
                  )}
                </Card>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Popular Articles + Need Help */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Popular Articles
              </Typography>
              <List>
                {filteredArticles.map((article, index) => (
                  <ListItem
                    key={index}
                    onClick={() => handleArticleClick(article)}
                    sx={{
                      px: 0, py: 2, borderBottom: '1px solid', borderColor: 'divider',
                      cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon>
                      <Description color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={article.title}
                      secondary={<Chip label={article.category} size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                    />
                    <ArrowForward color="action" />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: 'primary.dark', color: 'white', p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Need More Help?
                </Typography>
                <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
                  Can't find what you're looking for? Our support team is here to help.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      document.getElementById('contact-support')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  >
                    Contact Support
                  </Button>
                  <Button
                    variant="outlined"
                    href="https://app.cleolead.com"
                    sx={{
                      // On the dark ink card, the theme's default outlined style
                      // (white bg + ink text) renders white-on-white. Force a
                      // transparent fill with white text/border so it stays visible.
                      bgcolor: 'transparent',
                      color: '#FFFFFF',
                      borderColor: 'rgba(255,255,255,0.6)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.12)',
                        borderColor: '#FFFFFF',
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Open App
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default DocumentationPage;
